import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import { z } from "zod";

export type FeeScheduleJSON = z.infer<typeof FeeScheduleSchema.FeeScheduleJSONZ>;
export type FeeScheduleParams = z.infer<typeof FeeScheduleSchema.FeeScheduleZ>;

export default class FeeScheduleSchema {
  public static readonly FeeScheduleJSONZ = z.object({
    name: z.string().nullish(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ)
  });

  public static readonly FeeScheduleZ = z.object({
    name: z.string().optional(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryZ)
  });

  public static validate(values: unknown): FeeScheduleParams {
    return parseOrThrow(
      FeeScheduleSchema.FeeScheduleZ,
      values,
      { 
        logError: true,
        schemaName: "FeeSchedule",
        operation: "validate",
        payloadSample: values
      }
    );
  }

  public static validateJSON(json: unknown): FeeScheduleJSON {
    return parseOrThrow(
      FeeScheduleSchema.FeeScheduleJSONZ,
      json,
      { 
        logError: true,
        schemaName: "FeeSchedule",
        operation: "validateJSON",
        payloadSample: json
      }
    );
  }

  public static isValid(values: unknown): values is FeeScheduleParams {
    try {
      FeeScheduleSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FeeScheduleJSON {
    try {
      FeeScheduleSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleJSON): FeeScheduleParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      FeeScheduleSchema.FeeScheduleJSONZ,
      json,
      { 
        logError: true,
        schemaName: "FeeSchedule",
        operation: "deserialize",
        payloadSample: json
      }
    );
    return FeeScheduleSchema.codec.decode(parsed);
  }

  public static serialize(feeSchedule: FeeScheduleParams): FeeScheduleJSON {
    if (isNullOrUndefined(feeSchedule)) return feeSchedule;
    const parsed = FeeScheduleSchema.validate(feeSchedule);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeScheduleSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleSchema.FeeScheduleJSONZ,
    FeeScheduleSchema.FeeScheduleZ, {
    decode: (data) => ({
      name: data.name ?? undefined,
      categories: data.categories ? data.categories.map(FeeScheduleCategorySchema.deserialize) : []
    }),
    encode: (feeSchedule) => ({
      name: feeSchedule.name,
      categories: feeSchedule.categories ? feeSchedule.categories.map(FeeScheduleCategorySchema.serialize) : []
    })
  });
}
